/**
 * App Component
 *
 * Main application component with routing and providers.
 * Uses React.lazy() for code splitting and Suspense for loading states.
 */

import { lazy, Suspense } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from '@/components/layout';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * Lazy-loaded page components for code splitting
 */
const CatalogPage = lazy(() => import('@/pages/CatalogPage'));
const MicroorganismDetailPage = lazy(() => import('@/pages/MicroorganismDetailPage'));
const OrderPage = lazy(() => import('@/pages/OrderPage'));
const GrowthMediaPage = lazy(() => import('@/pages/GrowthMediaPage'));
const GrowthMediaDetailPage = lazy(() => import('@/pages/GrowthMediaDetailPage'));
const CMSPage = lazy(() => import('@/pages/CMSPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

/**
 * React Query client configuration with optimized defaults
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - data considered fresh
      gcTime: 30 * 60 * 1000, // 30 minutes - garbage collection time
      refetchOnWindowFocus: false, // Don't refetch on window focus
      retry: 1, // Only retry once on failure
    },
  },
});

/**
 * Page loading skeleton component
 */
function PageLoadingSkeleton() {
  return (
    <div className="container mx-auto px-4 py-8">
      <Skeleton className="mb-6 h-10 w-64" />
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="space-y-3">
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Main App component
 */
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          {/* Main layout with header and footer */}
          <Route element={<Layout />}>
            {/* Catalog page - serves as homepage */}
            <Route
              path="/"
              element={
                <Suspense fallback={<PageLoadingSkeleton />}>
                  <CatalogPage />
                </Suspense>
              }
            />

            {/* Catalog alias for backward compatibility */}
            <Route path="/catalog" element={<Navigate to="/" replace />} />

            {/* Microorganism detail page - uses slug for SEO-friendly URLs */}
            <Route
              path="/microorganism/:slug"
              element={
                <Suspense fallback={<PageLoadingSkeleton />}>
                  <MicroorganismDetailPage />
                </Suspense>
              }
            />

            {/* Order page */}
            <Route
              path="/order"
              element={
                <Suspense fallback={<PageLoadingSkeleton />}>
                  <OrderPage />
                </Suspense>
              }
            />

            {/* Growth media pages */}
            <Route
              path="/growth-media"
              element={
                <Suspense fallback={<PageLoadingSkeleton />}>
                  <GrowthMediaPage />
                </Suspense>
              }
            />
            <Route
              path="/growth-media/:id"
              element={
                <Suspense fallback={<PageLoadingSkeleton />}>
                  <GrowthMediaDetailPage />
                </Suspense>
              }
            />

            {/* CMS-managed static pages */}
            <Route
              path="/about"
              element={
                <Suspense fallback={<PageLoadingSkeleton />}>
                  <CMSPage slug="about" />
                </Suspense>
              }
            />
            <Route
              path="/contact"
              element={
                <Suspense fallback={<PageLoadingSkeleton />}>
                  <CMSPage slug="contact" />
                </Suspense>
              }
            />
            <Route
              path="/ordering"
              element={
                <Suspense fallback={<PageLoadingSkeleton />}>
                  <CMSPage slug="ordering" />
                </Suspense>
              }
            />
            <Route
              path="/shipping"
              element={
                <Suspense fallback={<PageLoadingSkeleton />}>
                  <CMSPage slug="shipping" />
                </Suspense>
              }
            />
            <Route
              path="/faq"
              element={
                <Suspense fallback={<PageLoadingSkeleton />}>
                  <CMSPage slug="faq" />
                </Suspense>
              }
            />
            <Route
              path="/privacy"
              element={
                <Suspense fallback={<PageLoadingSkeleton />}>
                  <CMSPage slug="privacy" />
                </Suspense>
              }
            />
            <Route
              path="/terms"
              element={
                <Suspense fallback={<PageLoadingSkeleton />}>
                  <CMSPage slug="terms" />
                </Suspense>
              }
            />

            {/* 404 page */}
            <Route
              path="*"
              element={
                <Suspense fallback={<PageLoadingSkeleton />}>
                  <NotFoundPage />
                </Suspense>
              }
            />
          </Route>
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
